import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, mxn, formatApiError } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tags, PackageX, Trash2, ImageOff, Save, Archive } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Nuevo", "Como nuevo", "Bueno", "Usado", "Para reparación"];
const th = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6A7D] font-semibold whitespace-nowrap";
const td = "px-3 py-2 text-sm text-[#0A2540] align-middle";

function Photo({ path, name }) {
  return path ? (
    <img src={fileUrl(path)} alt={name} className="w-12 h-12 object-cover rounded-sm border border-[#E2E8F0]" />
  ) : (
    <div className="w-12 h-12 rounded-sm border border-[#E2E8F0] bg-[#FAFAFA] flex items-center justify-center text-[#CBD5E1]"><ImageOff size={16} /></div>
  );
}

// ---------- Window 1: Control activos sobrantes (fijar precios) ----------
function SurplusPricing() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/surplus").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  const setField = (id, field, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [field]: value } }));
  const val = (r, field) => (edits[r.id]?.[field] !== undefined ? edits[r.id][field] : r[field]);

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(id, "photos", [data.path]);
      toast.success("Foto lista (guarda para aplicar)");
    } catch (e) { toast.error("No se pudo subir la foto"); }
  };

  const update = async () => {
    const ids = Object.keys(edits);
    if (ids.length === 0) { toast.info("No hay cambios"); return; }
    setSaving(true);
    try {
      for (const id of ids) {
        const body = { ...edits[id] };
        if (body.quantity !== undefined) body.quantity = parseInt(body.quantity, 10) || 0;
        if (body.internal_price !== undefined) body.internal_price = parseFloat(body.internal_price) || 0;
        await api.patch(`/admin/assets/${id}`, body);
      }
      toast.success("Sobrantes actualizados. Los cambios ya son visibles para todos.");
      setEdits({});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="surplus-pricing-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[980px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>
              <th className={th}>Activo</th><th className={th}>Foto</th><th className={th}>Cantidad</th><th className={th}>Unidad</th>
              <th className={th}>Proyecto</th><th className={th}>Estado del material</th><th className={th}>Precio unitario</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-[#5C6A7D]">No hay sobrantes activos.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`pricing-row-${r.id}`} className={edits[r.id] ? "bg-[#EFF6FF]/50" : ""}>
                <td className={td}><Input value={val(r, "name")} onChange={(e) => setField(r.id, "name", e.target.value)} className="h-9 rounded-sm min-w-[140px]" data-testid={`price-name-${r.id}`} /></td>
                <td className={td}>
                  <div className="flex items-center gap-2">
                    <Photo path={val(r, "photos")?.[0]} name={r.name} />
                    <label className="text-[10px] text-[#0052CC] cursor-pointer hover:underline">
                      cambiar<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(r.id, e.target.files?.[0])} />
                    </label>
                  </div>
                </td>
                <td className={td}><Input type="number" value={val(r, "quantity")} onChange={(e) => setField(r.id, "quantity", e.target.value)} className="h-9 rounded-sm w-20" data-testid={`price-qty-${r.id}`} /></td>
                <td className={td}><Input value={val(r, "unit") || ""} onChange={(e) => setField(r.id, "unit", e.target.value)} className="h-9 rounded-sm w-24" /></td>
                <td className={td}>
                  <Select value={val(r, "project_id") || ""} onValueChange={(v) => setField(r.id, "project_id", v)}>
                    <SelectTrigger className="h-9 rounded-sm min-w-[150px]"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className={td}>
                  <Select value={val(r, "condition") || ""} onValueChange={(v) => setField(r.id, "condition", v)}>
                    <SelectTrigger className="h-9 rounded-sm min-w-[140px]"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className={td}><Input type="number" step="0.01" value={val(r, "internal_price") ?? 0} onChange={(e) => setField(r.id, "internal_price", e.target.value)} className="h-9 rounded-sm w-28" data-testid={`price-value-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button onClick={update} disabled={saving} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11 px-6" data-testid="update-prices-btn"><Save size={15} className="mr-1.5" /> Actualizar</Button>
      </div>
    </div>
  );
}

// ---------- Window 2: Control de activos (eliminar) ----------
function AssetsControl() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/assets").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/admin/assets/${id}`); toast.success("Activo eliminado permanentemente"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar"); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto" data-testid="assets-control-tab">
      <table className="w-full min-w-[720px]">
        <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
          <tr><th className={th}>Foto</th><th className={th}>Activo</th><th className={th}>Proyecto</th><th className={th}>Cantidad</th><th className={th}>Estatus</th><th className={th}>Acción</th></tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-[#5C6A7D]">No hay activos de obra.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`asset-row-${r.id}`}>
              <td className={td}><Photo path={r.photos?.[0]} name={r.name} /></td>
              <td className={td}><div className="font-semibold">{r.name}</div><div className="text-xs text-[#5C6A7D]">{r.category || ""}</div></td>
              <td className={td}>{r.project_name}</td>
              <td className={td}>{r.quantity} {r.unit || ""}</td>
              <td className={td}><span className="text-xs uppercase tracking-[0.1em] text-[#5C6A7D]">{r.status}</span></td>
              <td className={td}>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="inline-flex items-center gap-1 text-xs text-[#9F1239] hover:underline" data-testid={`delete-asset-${r.id}`}><Trash2 size={13} /> Eliminar</button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar "{r.name}" permanentemente?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción no se puede deshacer. El activo se borrará de forma permanente.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(r.id)} className="bg-[#9F1239] hover:bg-[#881337]" data-testid={`confirm-delete-${r.id}`}>Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Window 3: Control de sobrantes (dar de baja) + bitácora ----------
function SurplusControl() {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const load = () => { api.get("/admin/surplus").then(({ data }) => setRows(data)); api.get("/admin/sales-log").then(({ data }) => setLog(data)); };
  useEffect(() => { load(); }, []);

  const retire = async (id) => {
    try { await api.post(`/admin/surplus/${id}/retire`); toast.success("Sobrante dado de baja. Se conserva en la bitácora de ventas."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo dar de baja"); }
  };

  return (
    <div className="space-y-6" data-testid="surplus-control-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr><th className={th}>Foto</th><th className={th}>Sobrante</th><th className={th}>Proyecto</th><th className={th}>Cantidad</th><th className={th}>Precio unitario</th><th className={th}>Acción</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-[#5C6A7D]">No hay sobrantes activos.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`surplus-row-${r.id}`}>
                <td className={td}><Photo path={r.photos?.[0]} name={r.name} /></td>
                <td className={td}><div className="font-semibold">{r.name}</div></td>
                <td className={td}>{r.project_name}</td>
                <td className={td}>{r.quantity} {r.unit || ""}</td>
                <td className={td}>{mxn(r.internal_price || 0)}</td>
                <td className={td}>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="inline-flex items-center gap-1 text-xs text-[#92400E] hover:underline" data-testid={`retire-surplus-${r.id}`}><PackageX size={13} /> Dar de baja</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Dar de baja "{r.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>Dejará de mostrarse a los demás usuarios, pero el registro se conserva en la bitácora de ventas.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => retire(r.id)} className="bg-[#92400E] hover:bg-[#78350F]" data-testid={`confirm-retire-${r.id}`}>Dar de baja</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]"><Archive size={13} /> Bitácora de ventas (dados de baja)</div>
        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
              <tr><th className={th}>Sobrante</th><th className={th}>Proyecto</th><th className={th}>Cantidad</th><th className={th}>Precio unitario</th><th className={th}>Fecha de baja</th></tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-testid="sales-log">
              {log.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-[#5C6A7D]">Sin ventas registradas.</td></tr>}
              {log.map((r) => (
                <tr key={r.id} data-testid={`log-row-${r.id}`}>
                  <td className={td}>{r.name}</td>
                  <td className={td}>{r.project_name}</td>
                  <td className={td}>{r.quantity} {r.unit || ""}</td>
                  <td className={td}>{mxn(r.internal_price || 0)}</td>
                  <td className={td}>{r.retired_at ? new Date(r.retired_at).toLocaleDateString("es-MX") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminInventory() {
  const { user } = useAuth();
  if (user && !hasPermission(user, "manage_assets")) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Administración</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Control de activos y sobrantes.</h1>
      </div>
      <Tabs defaultValue="prices">
        <TabsList>
          <TabsTrigger value="prices" data-testid="tab-prices"><Tags size={15} className="mr-1.5" /> Control activos sobrantes</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><Trash2 size={15} className="mr-1.5" /> Control de activos</TabsTrigger>
          <TabsTrigger value="surplus" data-testid="tab-surplus"><PackageX size={15} className="mr-1.5" /> Control de sobrantes</TabsTrigger>
        </TabsList>
        <TabsContent value="prices" className="mt-5"><SurplusPricing /></TabsContent>
        <TabsContent value="assets" className="mt-5"><AssetsControl /></TabsContent>
        <TabsContent value="surplus" className="mt-5"><SurplusControl /></TabsContent>
      </Tabs>
    </div>
  );
}
